/**
 * The weekly overtime split.
 *
 * THE RULE (Michael, 2026-08-25): every hourly employee gets 40 REGULAR hours per
 * payroll week; every hour beyond that is OVERTIME, posted under an overtime earning
 * at overtime rates. VISA employees are the exception — they are not eligible for
 * overtime, so all of their hours stay regular however many they work.
 *
 * WHY THIS EXISTS AT ALL: the current system sends overtime to NetSuite buried inside
 * ordinary time, and NetSuite does not derive the split itself. So WorkLogix has to
 * keep the running total and decide where regular ends — nothing downstream will.
 * Left alone, an employee working 58 hours is paid 58 regular hours.
 *
 * PURE and dependency-free, so the TimeBill poster and the Driver Logs grid share ONE
 * implementation. A second copy of a payroll rule is a second answer to the same
 * question.
 *
 * THE WEEK IS Wed → Tue, matching the payroll cycle. This module does not compute the
 * week boundary; the caller passes in exactly one week's entries, because only the
 * caller knows the tenant's payroll timezone (a business day is not a UTC day).
 */
import { EARNING_MAP } from './earning-map.generated.js';
/** Regular hours per payroll week before overtime begins. */
export const WEEKLY_REGULAR_HOURS_LIMIT = 40;
/**
 * Hours compare to this tolerance.
 *
 * Hours are decimals (12.12, 6.35) and summing them accumulates binary-float drift —
 * five entries can total 39.999999999999996 against a limit of 40, which would hand
 * someone a fraction of an hour of overtime they did not work, or withhold it. The
 * epsilon is far below any hours value anyone records (0.0000005 h is 1.8 ms) so it
 * can only ever absorb representation error, never a real quantity.
 */
const HOURS_EPSILON = 5e-7;
/** Round to 6 decimals — the same money-safe idiom the TimeBill poster uses. */
function round6(value) {
    return Math.round(value * 1e6) / 1e6;
}
/**
 * Payroll items that mark an employee as VISA staff.
 *
 * DERIVED from EARNING_MAP rather than hardcoded, so a VISA earning added in NetSuite
 * later is picked up by regenerating the map instead of by editing this rule. Today
 * that is HOURLY - DIRECT LABOR - VISA (254) and HOURLY - WAREHOUSE OP - VISA (257).
 *
 * Corroborating evidence that the exemption is real and not a policy quirk: NetSuite
 * contains NO VISA overtime earning at all. There is nothing for their overtime to
 * post to, because they are not meant to have any.
 */
let visaItemIds;
export function visaEarningItemIds() {
    // LAZY ON PURPOSE. Computing this at module load is a top-level side effect, which
    // pins EARNING_MAP into any bundle that so much as imports the shared barrel — and
    // that shipped the WHOLE NetSuite earnings catalogue (103 payroll items, names and
    // all) into the KIOSK PWA, an offline app installed on shop-floor devices that has
    // no business carrying payroll configuration. Behind a function the reference is
    // reachable only if something calls it, so tree-shaking can drop both.
    visaItemIds ??= new Set(EARNING_MAP.filter((m) => /\bVISA\b/i.test(m.name)).map((m) => m.nsPayrollItemId));
    return visaItemIds;
}
/**
 * Is this employee exempt from overtime?
 *
 * True when they hold any VISA earning on their NetSuite file. Determined from what
 * they HOLD rather than from a WorkLogix flag, so there is one source of truth and it
 * is the same one payroll edits.
 */
export function isOvertimeExempt(heldEarningIds) {
    const visa = visaEarningItemIds();
    for (const id of heldEarningIds) {
        if (visa.has(id)) {
            return true;
        }
    }
    return false;
}
/**
 * Split one employee's week into regular and overtime hours.
 *
 * ORDER DECIDES WHAT BECOMES OVERTIME. Entries are processed CHRONOLOGICALLY (by
 * `date`, ties broken by input order), because overtime is the time worked *after*
 * the 40th hour — not an arbitrary 40 hours picked from the week. Sorting by
 * anything else would move overtime onto a different day, and therefore potentially
 * onto a different department with a different rate.
 *
 * DEPARTMENTS ARE NEVER MERGED. A day that straddles the threshold is split IN PLACE,
 * keeping its department on both halves, so 30h in Warehouse then 15h in Driving
 * yields 40 regular (30 Warehouse + 10 Driving) and 5 overtime (Driving). The
 * earning resolves per department, so collapsing them would post the hours against
 * the wrong earning.
 *
 * VISA employees short-circuit: every hour is regular and `totalOvertime` is 0.
 *
 * @param entries one payroll week's worth of hours for ONE employee. The caller owns
 *   the week boundary, since a business day depends on the tenant's payroll timezone.
 * @param exempt whether this employee is overtime-exempt — see {@link isOvertimeExempt}.
 * @param regularAlreadyUsed regular hours of this SAME employee-week that are already
 *   accounted for elsewhere — hours previously posted, or hours from another source
 *   being posted in a different batch. Defaults to 0, which is the whole-week-at-once
 *   case and leaves every existing caller unchanged.
 *
 *   WHY IT EXISTS. `entries` is documented as "one payroll week's worth", but a caller
 *   that selects only what is UNPOSTED violates that the moment a week posts in two
 *   batches, and this function would then restart the 40-hour allowance from zero: 30h
 *   posted, 20h later, and the second batch reports 20 REGULAR instead of 10 regular +
 *   10 overtime. Ten hours of overtime, silently never paid. Seeding the allowance is
 *   what makes a partial batch add up to the same answer as the whole week.
 * @param regularLimit the weekly regular-hours allowance. Defaults to the standard
 *   {@link WEEKLY_REGULAR_HOURS_LIMIT}, which is every employee unless a per-employee
 *   PAY EXCEPTION names a different one — see `weeklyRegularHoursLimitWithExceptions`
 *   in pay-exception.ts. Taken as a parameter rather than read from the constant here
 *   so the override travels through ONE seam instead of becoming a second copy of the
 *   rule inside this function.
 */
export function splitWeeklyOvertime(entries, exempt, regularAlreadyUsed = 0, regularLimit = WEEKLY_REGULAR_HOURS_LIMIT) {
    // A non-positive or non-finite limit would turn every hour into overtime. Fall back
    // to the standard allowance rather than mis-splitting a whole week on bad input.
    const limit = Number.isFinite(regularLimit) && regularLimit > 0 ? regularLimit : WEEKLY_REGULAR_HOURS_LIMIT;
    // Stable chronological sort: `date` first, original position as the tie-break, so
    // two entries on the same day keep the order the caller gave them rather than
    // whatever the sort happens to do.
    const ordered = entries
        .map((entry, index) => ({ entry, index }))
        .sort((a, b) => a.entry.date === b.entry.date ? a.index - b.index : a.entry.date < b.entry.date ? -1 : 1);
    const split = new Map();
    // Clamped at the limit, never above it: a week that already holds 50 regular hours
    // has consumed the allowance, not overdrawn it, and a negative remainder would make
    // `remainingRegular` go negative and mis-split the next entry.
    let regularSoFar = Math.min(Math.max(0, Number.isFinite(regularAlreadyUsed) ? regularAlreadyUsed : 0), limit);
    for (const { entry, index } of ordered) {
        const hours = Number.isFinite(entry.hours) && entry.hours > 0 ? entry.hours : 0;
        if (exempt) {
            // Not eligible for overtime: every hour stays regular, however many.
            split.set(index, { ...entry, regularHours: round6(hours), overtimeHours: 0 });
            regularSoFar = round6(regularSoFar + hours);
            continue;
        }
        const remainingRegular = Math.max(0, limit - regularSoFar);
        // The epsilon absorbs float drift only — see HOURS_EPSILON. Without it a week
        // summing to 39.999999999999996 would leak a sliver of overtime.
        const regular = hours <= remainingRegular + HOURS_EPSILON ? hours : remainingRegular;
        const overtime = round6(hours - regular);
        split.set(index, {
            ...entry,
            regularHours: round6(regular),
            overtimeHours: overtime > HOURS_EPSILON ? overtime : 0,
        });
        regularSoFar = round6(regularSoFar + regular);
    }
    // Restore INPUT order: the caller supplied these in a meaningful order (a grid, a
    // list of sessions) and getting them back re-sorted would be a surprise.
    const lines = entries
        .map((_, index) => split.get(index))
        .filter((l) => l !== undefined);
    return {
        lines,
        totalRegular: round6(lines.reduce((sum, l) => sum + l.regularHours, 0)),
        totalOvertime: round6(lines.reduce((sum, l) => sum + l.overtimeHours, 0)),
        exempt,
    };
}
//# sourceMappingURL=overtime.js.map